/**
 * G5 (RC-6) — what a failed `generate-strategy` task can still be turned into.
 *
 * The owner's run `bglYzwVe4SXbUZPgWg7h` finished its expensive work and then died
 * while SAVING; the dialog showed «Необработанная ошибка CUSTOM_SUBTYPE_…» and a
 * Retry nothing listened to. The decision table (owner choice D3) is:
 *
 * | the task says                                   | the next step                                |
 * |-------------------------------------------------|----------------------------------------------|
 * | `errorCategory: 'incomplete_generation'`         | «Generate again» → G4b CONTINUE the run      |
 * | a persistence failure (category/code/legacy text)| «Save again» → G4a persist-from-task (apply) |
 * | anything else, and the run kept its parameters   | «Generate again» → a full NEW generation     |
 *
 * The order of the first two rows is load-bearing (final review FR-4): a run that saved some
 * blocks and then hit a persistence failure carries BOTH — and the answer for it is to
 * CONTINUE what was saved, not to re-save a run that kept no per-block state.
 *
 * No React here: the component renders what these functions decide, and the API
 * is injected so the surface can be tested without a client at all.
 */
import {
  describeStrategyPersistenceFailure,
  parseStrategyPersistenceFailure,
  STRATEGY_PERSISTENCE_ERROR_CODES,
} from '@modules/shared-types';
import type {
  AgentContinueStrategyGenerationRequest,
  AgentContinueStrategyGenerationResponse,
  AgentGenerateStrategyRequest,
  AgentGenerateStrategyResponse,
  AgentPersistStrategyFromTaskRequest,
  AgentPersistStrategyFromTaskResponse,
} from '@modules/shared-types';

/** The task type whose failures this module knows how to continue. */
export const STRATEGY_GENERATION_TASK_TYPE = 'generate-strategy';

/** The failure fields this module reads — a loose view of the Firestore task doc. */
export interface StrategyErrorTaskLike {
  id?: string;
  projectId?: string;
  strategyId?: string;
  userId?: string;
  taskType?: string;
  error?: string;
  errorCode?: string;
  errorCategory?: string;
  targetEntityPath?: { entityType?: string; frontendUrl?: string };
  /** Phase 4/H durable dispatch: the run's own parameters (Ruling GP-R20). */
  dispatch?: { execParams?: Record<string, unknown> };
}

export type StrategyFailureKind = 'persistence' | 'incomplete' | 'other';

export interface StrategyTaskFailure {
  kind: StrategyFailureKind;
  /** What the owner reads — the server's sentence, or one derived from a legacy task's raw text. */
  message: string;
  /** Present for a persistence failure. */
  code?: string;
}

/** The API surface the actions need — `AgentApiClient` satisfies it (DI at the boundary). */
export interface StrategyResolveApi {
  persistStrategyFromTask(request: AgentPersistStrategyFromTaskRequest): Promise<AgentPersistStrategyFromTaskResponse>;
  continueStrategyGeneration(request: AgentContinueStrategyGenerationRequest): Promise<AgentContinueStrategyGenerationResponse>;
  generateStrategy(request: AgentGenerateStrategyRequest): Promise<AgentGenerateStrategyResponse>;
}

export interface StrategyResolveOutcome {
  severity: 'success' | 'warning' | 'error';
  /** One line per thing the server said — every refusal verbatim. */
  lines: string[];
}

function isPersistenceFailure(task: StrategyErrorTaskLike): boolean {
  if (task.errorCategory === 'persistence') return true;
  if (task.errorCode && (STRATEGY_PERSISTENCE_ERROR_CODES as readonly string[]).includes(task.errorCode)) return true;
  // Ruling GP-R11: a task written before the mapping carries only the raw text.
  return Boolean(parseStrategyPersistenceFailure(task.error));
}

/**
 * The failure of a `generate-strategy` task, or `undefined` for any other task
 * type (whose next step belongs to the surface that owns it, not to this module).
 */
export function classifyStrategyTaskFailure(task: StrategyErrorTaskLike): StrategyTaskFailure | undefined {
  if (task.taskType !== STRATEGY_GENERATION_TASK_TYPE) return undefined;
  const raw = task.error ?? '';
  // Final review FR-4: `incomplete_generation` WINS over a persistence `errorCode`.
  // `strategyGenerationFailureTaskFields` can set both (a persistence failure on a run that
  // had already saved blocks — GP-R27), and reading the code first offered «Save again» for
  // such a task: a G3a-era recovery that refuses every block with `block_not_accepted`,
  // because the run saved them as it went and kept no per-block state to rebuild from. The
  // category is the one the server derived LAST, and it says continue.
  if (task.errorCategory === 'incomplete_generation') return { kind: 'incomplete', message: raw };
  if (isPersistenceFailure(task)) {
    const parsed = parseStrategyPersistenceFailure(raw);
    return {
      kind: 'persistence',
      // The server composes the sentence at the moment of failure (it knows what
      // was kept); only a LEGACY document has to be spoken for here.
      message: task.errorCode || !parsed ? raw : describeStrategyPersistenceFailure(parsed),
      code: task.errorCode ?? parsed?.code,
    };
  }
  return { kind: 'other', message: raw };
}

const FULL_GENERATION_FIELDS = [
  // Fix round 1 (M4): `blockGenerationMode` travels too — the repeated run keeps
  // the dispatch mode the original was FROZEN with instead of silently falling
  // back to the default.
  'blockGenerationMode',
  'description', 'toneOfVoice', 'designDescription', 'strategyVariables', 'model', 'maxTokens',
  'evalMaxIterations', 'evalEnabled', 'generateScreens', 'autoRepair', 'healthProfile',
  'insightsAnalysisId', 'insightSelection', 'includeInsightSources', 'insightVariableGenerationId',
  'variableIds', 'insightVariableIds', 'useProjectVariables', 'predecessorStrategyId',
  'predecessorVersion', 'forkHypothesis', 'changeDiff', 'availableBlocks', 'libraryBlockSelections',
] as const;

/**
 * Ruling GP-R20: «Generate again» repeats the run's OWN parameters or it is not
 * offered at all — a generation started from half-remembered fields is a second
 * bill for something the owner did not ask for. Returns `undefined` when the task
 * never persisted its `dispatch.execParams` (oversized payloads run inline).
 */
export function fullGenerationRequestFromExecParams(
  execParams: Record<string, unknown> | undefined,
): AgentGenerateStrategyRequest | undefined {
  if (!execParams) return undefined;
  const required = ['projectId', 'strategyId', 'userId', 'name', 'strategyType'] as const;
  if (required.some((field) => typeof execParams[field] !== 'string' || !execParams[field])) return undefined;
  if (!execParams.sourceData || typeof execParams.sourceData !== 'object') return undefined;

  const request: Record<string, unknown> = {
    projectId: execParams.projectId,
    strategyId: execParams.strategyId,
    userId: execParams.userId,
    name: execParams.name,
    strategyType: execParams.strategyType,
    sourceData: execParams.sourceData,
  };
  // The executor calls it `providerType`; the route takes `provider`.
  if (typeof execParams.providerType === 'string') request.provider = execParams.providerType;
  for (const field of FULL_GENERATION_FIELDS) {
    if (execParams[field] !== undefined) request[field] = execParams[field];
  }
  return request as unknown as AgentGenerateStrategyRequest;
}

function describePersistResult(response: AgentPersistStrategyFromTaskResponse): StrategyResolveOutcome {
  if (!response?.success) {
    // Fix round 1 (M9): a non-200 answer can still name the blocks it refused —
    // those reasons are the actionable half and must not be dropped.
    return {
      severity: 'error',
      lines: [
        response?.error || 'The strategy could not be saved.',
        ...(response?.refusals ?? []).map((refusal) => refusal.message),
      ],
    };
  }
  if (response.refusals?.length) {
    // AC-GP-021: nothing was written, and each reason is the server's own sentence.
    return { severity: 'warning', lines: response.refusals.map((refusal) => refusal.message) };
  }
  if (response.alreadyPersisted) {
    return { severity: 'success', lines: ['This generation was already saved — nothing was written.'] };
  }
  const lines = [`Saved ${response.blocks ?? 0} block(s) and ${response.screens ?? 0} screen(s).`];
  if (response.incompleteReason) lines.push(response.incompleteReason);
  if (response.retryable && response.failedBlockIds?.length) {
    // FR-1: true as written — a retry adopts the blocks this attempt saved at their
    // existing ids and writes only the ones named here.
    lines.push(`Not saved yet: ${response.failedBlockIds.join(', ')} — pressing «Save again» retries exactly these, and leaves the blocks already saved as they are.`);
  }
  for (const gap of response.gaps ?? []) lines.push(gap.message);
  return { severity: response.retryable ? 'warning' : 'success', lines };
}

function describeContinueResult(response: AgentContinueStrategyGenerationResponse): StrategyResolveOutcome {
  if (!response?.success) {
    return { severity: 'error', lines: [response?.error || 'The generation could not be started.'] };
  }
  const lines: string[] = [];
  if (response.mode === 'full') {
    if (response.reason) lines.push(response.reason);
    lines.push(`Started a full generation — new task ${response.taskId ?? ''}.`.trim());
  } else {
    lines.push(`Generation continued — new task ${response.taskId ?? ''}.`.trim());
  }
  for (const warning of response.warnings ?? []) lines.push(warning);
  return { severity: 'success', lines };
}

function describeGenerateResult(response: AgentGenerateStrategyResponse): StrategyResolveOutcome {
  if (!response?.success) {
    return { severity: 'error', lines: [response?.error || 'The generation could not be started.'] };
  }
  const lines = [`New generation started — task ${response.taskId ?? ''}.`.trim()];
  for (const warning of response.warnings ?? []) lines.push(warning);
  return { severity: 'success', lines };
}

function failureOutcome(error: unknown): StrategyResolveOutcome {
  return { severity: 'error', lines: [error instanceof Error ? error.message : String(error)] };
}

/** «Save again» — G4a's recovery, which refuses or applies in ONE request (no provider call). */
export async function saveStrategyAgain(
  task: StrategyErrorTaskLike,
  api: StrategyResolveApi,
): Promise<StrategyResolveOutcome> {
  if (!task.projectId || !task.strategyId || !task.id) {
    return { severity: 'error', lines: ['This task does not name the project and strategy to save into.'] };
  }
  try {
    return describePersistResult(await api.persistStrategyFromTask({
      projectId: task.projectId,
      strategyId: task.strategyId,
      taskId: task.id,
      mode: 'apply',
    }));
  } catch (error) {
    return failureOutcome(error);
  }
}

/**
 * «Generate again» — a CONTINUE for a run that saved part of itself (G4b), a full
 * generation from the run's own parameters for anything else.
 */
export async function generateStrategyAgain(
  task: StrategyErrorTaskLike,
  failure: StrategyTaskFailure,
  api: StrategyResolveApi,
): Promise<StrategyResolveOutcome> {
  try {
    if (failure.kind === 'incomplete') {
      if (!task.projectId || !task.strategyId || !task.userId || !task.id) {
        return { severity: 'error', lines: ['This task does not name the run to continue.'] };
      }
      return describeContinueResult(await api.continueStrategyGeneration({
        projectId: task.projectId,
        strategyId: task.strategyId,
        userId: task.userId,
        continueFromTaskId: task.id,
      }));
    }
    const request = fullGenerationRequestFromExecParams(task.dispatch?.execParams);
    if (!request) {
      return { severity: 'error', lines: ['This run did not keep its parameters, so it cannot be repeated from here.'] };
    }
    return describeGenerateResult(await api.generateStrategy(request));
  } catch (error) {
    return failureOutcome(error);
  }
}

/** The label of the one action a failure kind offers (the resolver renders exactly one). */
export function strategyActionLabel(kind: StrategyFailureKind): 'Save again' | 'Generate again' {
  return kind === 'persistence' ? 'Save again' : 'Generate again';
}
